/**
 * BSA API Client — Barracuda Strip Analyzer
 * Uses Shopify App Proxy routes (/apps/bsa/api/*) for all requests.
 * No CORS needed — same-origin through Shopify proxy.
 */

(function () {
  "use strict";

  const PROXY = "/apps/bsa/api";

  const API = {
    _customerId: null,

    init({ customerId }) {
      this._customerId = customerId;
      if (customerId) {
        this.loadCustomerProfile();
      }
    },

    // ─── HTTP helpers ─────────────────────────────────────────────

    async _fetch(url, options = {}) {
      const res = await fetch(url, options);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || `Erreur ${res.status}`);
      }
      return res.json();
    },

    // ─── Customer ─────────────────────────────────────────────────

    async loadCustomerProfile() {
      if (!this._customerId) return null;
      try {
        const data = await this._fetch(`${PROXY}/customer?customer_id=${this._customerId}`);
        this._profile = data;
        document.dispatchEvent(
          new CustomEvent("bsa:customer-loaded", { detail: data })
        );
        return data;
      } catch (e) {
        console.error("[BSA] Erreur chargement profil:", e);
        return null;
      }
    },

    // ─── Bassins ──────────────────────────────────────────────────

    async getBassins() {
      if (!this._customerId) return [];
      const data = await this._fetch(`${PROXY}/bassins?customer_id=${this._customerId}`);
      return data.bassins || [];
    },

    async getBassin(id) {
      const data = await this._fetch(`${PROXY}/bassins?id=${encodeURIComponent(id)}`);
      return data.bassin;
    },

    async createBassin(fields) {
      const data = await this._fetch(`${PROXY}/bassins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...fields,
          customer_id: this._customerId,
        }),
      });
      document.dispatchEvent(
        new CustomEvent("bsa:bassin-created", { detail: data.bassin })
      );
      return data.bassin;
    },

    async updateBassin(id, fields) {
      const data = await this._fetch(`${PROXY}/bassins`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, ...fields }),
      });
      document.dispatchEvent(
        new CustomEvent("bsa:bassin-updated", { detail: data.bassin })
      );
      return data.bassin;
    },

    async deleteBassin(id) {
      const data = await this._fetch(`${PROXY}/bassins?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      document.dispatchEvent(
        new CustomEvent("bsa:bassin-deleted", { detail: { id } })
      );
      return data;
    },

    // ─── Analyses ─────────────────────────────────────────────────

    async getAnalyses({ bassinId = null, limit = 10, cursor = null } = {}) {
      let url = `${PROXY}/analyses?customer_id=${this._customerId}&limit=${limit}`;
      if (bassinId) url += `&bassin_id=${encodeURIComponent(bassinId)}`;
      if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
      return this._fetch(url);
    },

    async getAnalyse(id) {
      const data = await this._fetch(`${PROXY}/analyses?id=${encodeURIComponent(id)}`);
      return data.analyse;
    },

    async saveAnalyse(analyseData) {
      const data = await this._fetch(`${PROXY}/analyses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...analyseData,
          customer_id: this._customerId,
        }),
      });
      document.dispatchEvent(
        new CustomEvent("bsa:analyse-saved", { detail: data.analyse })
      );
      return data.analyse;
    },

    // ─── Cart helpers ─────────────────────────────────────────────

    async addToCart(variantId, qty = 1) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      });
      if (!res.ok) throw new Error("Erreur ajout au panier");
      const data = await res.json();
      document.dispatchEvent(new CustomEvent("bsa:cart-updated", { detail: data }));
      return data;
    },

    async getProductByHandle(handle) {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return res.json();
    },
  };

  window.BSA_API = API;
})();
